import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, Btn, Select, Empty } from "@/components/ui-kit";
import { Logo } from "@/components/Logo";
import { Eye, FileSpreadsheet, FileText, Printer } from "lucide-react";

const REPORTS = [
  ["register", "Payroll Register"], ["wage_statement", "Wage Statement"],
  ["salary_summary", "Salary Summary"], ["bank_sheet", "Bank Sheet"],
  ["pf_report", "PF Report"], ["esi_report", "ESI Report"], ["pt_report", "PT Report"],
  ["employer_contribution", "Employer Contribution"], ["uan_register", "Employee / UAN Register"],
  ["service_charge_statement", "Service Charge Statement"],
];

export default function Reports() {
  const [searchParams] = useSearchParams();

  const queryMonth =
    searchParams.get("month") || "";

  const queryClient =
    searchParams.get("client_id") || "";

  const queryReport =
    searchParams.get("report") || "register";

  const [runs, setRuns] = useState([]);
  const [clients, setClients] = useState([]);
  const [runId, setRunId] = useState("");
  const [report, setReport] = useState(queryReport);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payroll/runs").then((r) =>
      setRuns(
        r.data.filter((x) =>
          [
            "calculated",
            "finalized",
            "locked",
          ].includes(x.status)
        )
      )
    );

    api.get("/clients").then((r) =>
      setClients(r.data)
    );
  }, []);

  useEffect(() => {
    if (!runs.length || runId) {
      return;
    }

    const matching = runs.find((r) => {
      if (
        queryMonth &&
        r.month !== queryMonth
      ) {
        return false;
      }

      if (
        queryClient &&
        queryClient !== "all" &&
        r.client_id !== queryClient
      ) {
        return false;
      }

      return true;
    });

    if (matching) {
      setRunId(matching.id);
    }
  }, [
    runs,
    runId,
    queryMonth,
    queryClient,
  ]);

  const cname = (id) =>
    clients.find((c) => c.id === id)?.name || "";
  const run = runs.find((r) => r.id === runId);

  const reportPeriods = [
    ...new Set(
      runs
        .map((r) => r.month)
        .filter(Boolean)
    ),
  ].sort().reverse();

  const selectedPeriod =
    run?.month ||
    queryMonth ||
    "";

  const periodRuns =
    selectedPeriod
      ? runs.filter(
          (r) =>
            r.month ===
            selectedPeriod
        )
      : runs;

  const changePeriod = (value) => {
    const first = runs.find(
      (r) =>
        r.month === value &&
        (
          !queryClient ||
          queryClient === "all" ||
          r.client_id ===
            queryClient
        )
    );

    setRunId(
      first?.id || ""
    );

    setPreview(null);
  };

  const loadPreview = async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/runs/${runId}/report`, { params: { report } });
      setPreview(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { setPreview(null); }, [runId, report]);

  const printPreview = () => {
    const el = document.getElementById("report-print-area");
    if (!el) return;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${preview?.title}</title>
      <style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left}th{background:#1e3a8a;color:#fff}
      h2{margin:0}p{color:#475569}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Preview on screen, then export Excel / CSV / PDF / Print" />
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="nk-overline block mb-1.5">
              Payroll Month / Period
            </label>

            <Select
              value={selectedPeriod}
              onChange={(e) =>
                changePeriod(
                  e.target.value
                )
              }
              data-testid="report-period-select"
            >
              <option value="">
                All Periods
              </option>

              {reportPeriods.map(
                (m) => (
                  <option
                    key={m}
                    value={m}
                  >
                    {m}
                  </option>
                )
              )}
            </Select>
          </div>

          <div>
            <label className="nk-overline block mb-1.5">
              Payroll Run / Client
            </label>

            <Select
              value={runId}
              onChange={(e) =>
                setRunId(
                  e.target.value
                )
              }
              data-testid="report-run-select"
            >
              <option value="">
                Select client / run
              </option>

              {periodRuns.map(
                (r) => (
                  <option
                    key={r.id}
                    value={r.id}
                  >
                    {cname(r.client_id)} — {r.month} ({r.employee_count})
                  </option>
                )
              )}
            </Select>
          </div>
          <div>
            <label className="nk-overline block mb-1.5">Report</label>
            <Select value={report} onChange={(e) => setReport(e.target.value)} data-testid="report-type-select">
              {REPORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
          </div>
          <Btn onClick={loadPreview} disabled={!runId || loading} data-testid="preview-btn"><Eye size={16} /> Preview</Btn>
        </div>
      </Card>

      {preview ? (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <div className="nk-head font-semibold text-sm">{preview.title} — Preview</div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/report-export?report=${report}&format=xlsx`, `${report}_${run?.month}.xlsx`)} data-testid="export-xlsx"><FileSpreadsheet size={14} /> Excel</Btn>
              <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/report-export?report=${report}&format=csv`, `${report}_${run?.month}.csv`)} data-testid="export-csv">CSV</Btn>
              <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/report-export?report=${report}&format=pdf`, `${report}_${run?.month}.pdf`)} data-testid="export-pdf"><FileText size={14} /> PDF</Btn>
              <Btn variant="outline" onClick={printPreview} data-testid="print-btn"><Printer size={14} /> Print</Btn>
            </div>
          </div>
          <div className="p-4">
            <div id="report-print-area">
              <div className="flex items-center justify-between mb-3">
                <Logo onLight height={40} />
                <div className="text-right">
                  <h2 className="nk-head font-bold text-lg">
                    {preview.title}
                  </h2>

                  <p className="text-sm text-slate-500">
                    Client: {cname(run?.client_id) || "—"}
                  </p>

                  <p className="text-sm text-slate-500">
                    Payroll Period: {run?.month || "—"}
                  </p>

                  {preview.subtitle && (
                    <p className="text-xs text-slate-400">
                      {preview.subtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-200 rounded-md">
                <table className="nk-table" data-testid="report-preview-table">
                  <thead><tr>{preview.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.rows.map((row, ri) => (
                      <tr key={ri}>{row.map((cell, ci) => <td key={ci} className={ci > 0 ? "nk-num" : ""}>{cell}</td>)}</tr>
                    ))}
                  </tbody>
                  {preview.totals && (
                    <tfoot><tr className="font-bold bg-slate-100">
                      {preview.totals.map((cell, ci) => <td key={ci} className={ci > 0 ? "nk-num" : ""}>{cell}</td>)}
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </Card>
      ) : <Empty title="Select a run and report" hint="Click Preview to view before exporting." />}
    </div>
  );
}
